import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

declare const process: { env: Record<string, string | undefined> };
const API = `${process.env.REACT_APP_BACKEND_URL}/api/pureh/blend`;

type Mat = {
  id: string; name: string; code: string; category: string; unit: string;
  stockOnHand: number | null; reorderPoint: number | null; low: boolean;
  costByMarket: Record<string, { value: number | null; currency: string }> | null;
  needsPrice: boolean;
};

type EditRow = { stock: string; reorder: string; costEg: string; costSa: string };

export default function InventoryPage() {
  const [mats, setMats] = useState<Mat[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState<Record<string, EditRow>>({});
  const [search, setSearch] = useState("");
  const [onlyNeedsPrice, setOnlyNeedsPrice] = useState(false);

  const load = () =>
    fetch(`${API}/inventory`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(setMats).catch((e) => setErr(String(e.message || e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async (m: Mat) => {
    const e = edit[m.id];
    if (!e) return;
    try {
      const res = await fetch(`${API}/inventory/${m.id}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stockOnHand: e.stock === "" ? null : Number(e.stock),
          reorderPoint: e.reorder === "" ? null : Number(e.reorder),
          costEg: e.costEg === "" ? null : Number(e.costEg),
          costSa: e.costSa === "" ? null : Number(e.costSa),
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || res.statusText);
      toast.success(`اتحدّث ${m.name}`);
      setEdit((p) => { const c = { ...p }; delete c[m.id]; return c; });
      load();
    } catch (er: any) { toast.error(String(er.message || er)); }
  };

  const filtered = useMemo(() => {
    let rows = mats;
    if (onlyNeedsPrice) rows = rows.filter((m) => m.needsPrice);
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((m) => `${m.name} ${m.code} ${m.category}`.toLowerCase().includes(q));
    return rows;
  }, [mats, search, onlyNeedsPrice]);

  if (loading) return <div className="bl-sub py-8 text-center" data-testid="inventory-loading">جارٍ التحميل…</div>;
  if (err) return <div className="bl-sub py-8 text-center" style={{ color: "var(--bl-danger)" }} data-testid="inventory-error">{err}</div>;

  const low = mats.filter((m) => m.low);
  const needsCount = mats.filter((m) => m.needsPrice).length;

  return (
    <div className="space-y-4" data-testid="inventory-page">
      {low.length > 0 && (
        <div className="bl-card" style={{ borderColor: "rgba(224,122,106,0.5)" }} data-testid="low-stock-board">
          <div className="bl-eyebrow mb-2" style={{ color: "var(--bl-danger)" }}>⚠️ خامات قربت تخلص</div>
          <div className="flex gap-2 flex-wrap">
            {low.map((m) => (
              <span key={m.id} className="bl-pill danger" data-testid={`low-stock-${m.id}`}>
                {m.name}: {m.stockOnHand} جم (حد الطلب {m.reorderPoint})
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="bl-card">
        <div className="bl-eyebrow mb-2">المخزون — مكتبة الخامات الحقيقية ({mats.length} مادة · مصدر الأسعار الوحيد)</div>
        <div className="flex gap-2 items-center flex-wrap mb-3">
          <input className="bl-input" style={{ maxWidth: 260 }} placeholder="ابحث: اسم / كود / تصنيف…"
            value={search} onChange={(e) => setSearch(e.target.value)} data-testid="inventory-search-input" />
          {needsCount > 0 && (
            <button className={`bl-tab ${onlyNeedsPrice ? "active" : ""}`} onClick={() => setOnlyNeedsPrice(!onlyNeedsPrice)}
              data-testid="inventory-needs-price-filter">💰 يحتاج سعر ({needsCount})</button>
          )}
          <a className="bl-btn ghost" style={{ padding: "6px 12px", textDecoration: "none" }}
            href={`${API}/materials/needs-price.xlsx`} data-testid="inventory-download-price-sheet">⬇ شيت الأسعار</a>
          <label className="bl-btn ghost" style={{ padding: "6px 12px", cursor: "pointer" }} data-testid="inventory-upload-price-sheet">
            ⬆ رفع الشيت
            <input type="file" accept=".xlsx" style={{ display: "none" }}
              onChange={async (ev) => {
                const f = ev.target.files?.[0];
                if (!f) return;
                const fd = new FormData();
                fd.append("file", f);
                try {
                  const res = await fetch(`${API}/materials/price-sheet`, { method: "POST", credentials: "include", body: fd });
                  const j = await res.json();
                  if (!res.ok) throw new Error(j.detail || res.statusText);
                  toast.success(`اتحدّث ${j.updated} خامة${j.skipped.length ? ` · اتعدّى ${j.skipped.length}` : ""}`);
                  load();
                } catch (er: any) { toast.error(String(er.message || er)); }
                ev.target.value = "";
              }} />
          </label>
          <span className="bl-sub mr-auto" data-testid="inventory-count">{filtered.length} صف</span>
        </div>
        <table className="bl-table" data-testid="inventory-table">
          <thead>
            <tr><th>الخامة</th><th>EGP/جم</th><th>SAR/جم</th><th>المخزون (جم)</th><th>حد الطلب (جم)</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.map((m) => {
              const e = edit[m.id];
              return (
                <tr key={m.id} data-testid={`inventory-row-${m.id}`}
                  style={m.low ? { background: "rgba(224,122,106,0.06)" } : undefined}>
                  <td className="bl-en">
                    {m.name}
                    {m.low && <span className="bl-pill danger mr-1">منخفض</span>}
                    {m.needsPrice && <span className="bl-pill mr-1" style={{ color: "#e8c56a" }} data-testid={`needs-price-${m.id}`}>يحتاج سعر</span>}
                  </td>
                  <td>
                    {e ? <input type="number" className="bl-input" style={{ width: 80 }} value={e.costEg}
                      onChange={(ev) => setEdit((p) => ({ ...p, [m.id]: { ...e, costEg: ev.target.value } }))}
                      data-testid={`inv-cost-eg-input-${m.id}`} />
                      : (m.costByMarket?.eg?.value ?? "—")}
                  </td>
                  <td>
                    {e ? <input type="number" className="bl-input" style={{ width: 80 }} value={e.costSa}
                      onChange={(ev) => setEdit((p) => ({ ...p, [m.id]: { ...e, costSa: ev.target.value } }))}
                      data-testid={`inv-cost-sa-input-${m.id}`} />
                      : (m.costByMarket?.sa?.value ?? "—")}
                  </td>
                  <td>
                    {e ? <input type="number" className="bl-input" style={{ width: 90 }} value={e.stock}
                      onChange={(ev) => setEdit((p) => ({ ...p, [m.id]: { ...e, stock: ev.target.value } }))}
                      data-testid={`inv-stock-input-${m.id}`} />
                      : (m.stockOnHand ?? "—")}
                  </td>
                  <td>
                    {e ? <input type="number" className="bl-input" style={{ width: 90 }} value={e.reorder}
                      onChange={(ev) => setEdit((p) => ({ ...p, [m.id]: { ...e, reorder: ev.target.value } }))}
                      data-testid={`inv-reorder-input-${m.id}`} />
                      : (m.reorderPoint ?? "—")}
                  </td>
                  <td>
                    {e ? (
                      <span className="flex gap-1">
                        <button className="bl-btn primary" style={{ padding: "4px 12px" }} onClick={() => save(m)} data-testid={`inv-save-${m.id}`}>حفظ</button>
                        <button className="bl-btn ghost" style={{ padding: "4px 12px" }}
                          onClick={() => setEdit((p) => { const c = { ...p }; delete c[m.id]; return c; })}>إلغاء</button>
                      </span>
                    ) : (
                      <button className="bl-btn ghost" style={{ padding: "4px 12px" }} data-testid={`inv-edit-${m.id}`}
                        onClick={() => setEdit((p) => ({ ...p, [m.id]: {
                          stock: m.stockOnHand?.toString() ?? "", reorder: m.reorderPoint?.toString() ?? "",
                          costEg: m.costByMarket?.eg?.value?.toString() ?? "", costSa: m.costByMarket?.sa?.value?.toString() ?? "",
                        } }))}>
                        تعديل
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="bl-sub mt-2" style={{ fontSize: 10 }}>تأكيد أي تشغيلة من الحاسبة يخصم الجرامات المستهلكة أوتوماتيك من هنا · سعر EG/SA بيدخل مباشرة في COGS كل التركيبات والطلبات.</p>
      </div>
    </div>
  );
}
